/**
 * Manifiesto del portafolio.
 *
 * SOLO FOTOS PROPIAS. La política del sitio es que no se publica banco
 * de imágenes como portafolio, ni temporalmente: un estudio de fotografía
 * cuyo portafolio es stock no tiene un problema de contenido, tiene un
 * problema de existencia.
 *
 * Donde no hay fotos, los componentes de foto pintan un marcador con el
 * aspect-ratio correcto (ver components/fotografia/PhotoImage.js) y los
 * segmentos con menos de MINIMO_FOTOS quedan como "próximamente", con noindex
 * y fuera del sitemap. Así "faltan fotos" es un estado del sistema y no una
 * fuga de credibilidad.
 *
 * CÓMO SE LLENA
 *   1. Deja los originales de cámara en public/fotografia/_originales/<segmento>/
 *   2. npm run fotos      → genera los WebP y escribe el manifiesto candidato
 *   3. Pega el resultado en FOTOS y escribe cada `alt` a mano.
 *
 * El `alt` no se autogenera: es SEO de Google Imágenes y accesibilidad. Debe
 * describir lo que se ve, en español, mencionando el sujeto con naturalidad
 * ("Plato de ceviche sobre mesa de madera en el salón del restaurante"), no
 * repetir palabras clave.
 *
 * Campos por foto:
 *   id        único y estable (lo usa React como key)
 *   src       ruta pública del WebP de ancho medio
 *   srcSet    variantes por ancho, generadas por el script
 *   w, h      dimensiones reales, medidas con ffprobe
 *   ratio     h/w — para reservar el espacio y evitar CLS
 *   segmento  id de segmentos.js: "eventos", "bodas" o "estudio"
 *   label     texto corto que aparece al pasar el cursor
 *   alt       escrito a mano
 *   featured  true si puede aparecer en el hero o en la tira de prueba visual
 */

/** Fotos reales mínimas para que un segmento se considere publicable. */
export const MINIMO_FOTOS = 3;

/**
 * Constructor de una foto del manifiesto. Los WebP viven en
 * public/fotografia/<carpeta>/ en los tres anchos del script.
 *
 * `carpeta` y `segmento` van separados porque los archivos de la primera tanda
 * quedaron en /fotografia/familia/ con prefijo "familia-", de cuando el sitio
 * tenía siete segmentos. Moverlos cambiaría sus URLs públicas, así que se
 * quedan ahí y solo cambia a qué servicio pertenecen.
 */
const fotoDe = (carpeta, segmento) => (nombre, w, h, label, alt, { featured = false } = {}) => {
  const id = `${carpeta}-${nombre}`;
  const base = `/fotografia/${carpeta}/${id}`;
  return {
    id,
    src: `${base}-960.webp`,
    srcSet: [480, 960, 1600].map((ancho) => `${base}-${ancho}.webp ${ancho}w`).join(", "),
    w,
    h,
    ratio: Number((h / w).toFixed(4)),
    segmento,
    label,
    alt,
    featured,
  };
};

const evento = fotoDe("familia", "eventos");
const estudio = fotoDe("familia", "estudio");
/* Las que se publican desde ahora ya van en la carpeta de su servicio. */
const eventoNuevo = fotoDe("eventos", "eventos");
const boda = fotoDe("bodas", "bodas");

/*
 * Embarazo de Oriana. Dos orígenes, que son justo los dos servicios:
 *
 *   - EVENTOS: las entregas privadas (data/entregas.js) — revelación de
 *     embarazo (17·01·2026), revelación de sexo (21·03·2026) y baby shower
 *     (06·06·2026).
 *   - ESTUDIO: la sesión de embarazo en estudio, en blanco y negro, toda
 *     vertical 2:3.
 *
 * Oriana autorizó de palabra; la firma en /entregas/<evento>/autorizacion (la
 * de ella y la de su pareja, que sale en las de las entregas) tiene que quedar
 * registrada en omega_autorizaciones antes del deploy (docs/proceso-entrega.md,
 * paso 5).
 *
 * De las entregas solo van encuadres donde aparecen ellos dos o el montaje.
 * Quedan fuera, a propósito, las fotos con invitados (cada uno tendría que
 * firmar), las de la ecografía y la mesa con el apellido en el cartel (dato
 * sensible, ver docs/autorizacion-uso-imagen.md).
 *
 * `featured` son las de la tira del home, que va en blanco y negro y en
 * celdas verticales iguales: por eso solo se destacan verticales.
 */
const V = [3456, 5184]; // verticales 2:3 a cuadro completo
const H = [5184, 3456];
const DESTACADA = { featured: true };

export const FOTOS = [
  /* Portada del home. El periódico lleva el apellido de la familia y la
     ecografía: se publica por decisión expresa del estudio (11·10·2026). */
  eventoNuevo("revelacion-periodico", 2000, 1333, "Revelación de embarazo",
    "Una pareja escondida detrás de un periódico que anuncia «Nuevas noticias: ¡Vamos a ser papás!», con la ecografía impresa"),
  evento("baby-shower-beso", ...H, "Baby shower",
    "El papá besa en la mejilla a la mamá, que se toma la barriga frente a un arco de globos azules"),
  estudio("embarazo-sonrisa", ...V, "Sesión de embarazo",
    "Retrato en blanco y negro de una embarazada que sonríe a cámara con una mano sobre la barriga",
    DESTACADA),
  estudio("embarazo-perfil", ...V, "Sesión de embarazo",
    "Embarazada de perfil con los ojos cerrados y una mano en la cintura, en blanco y negro",
    DESTACADA),
  estudio("embarazo-rosas", ...V, "Sesión de embarazo",
    "Embarazada riendo con un ramo de rosas blancas apoyado sobre la barriga",
    DESTACADA),
  estudio("embarazo-barriga", ...V, "Sesión de embarazo",
    "Detalle de las manos de una embarazada rodeando la barriga, con un blazer negro abierto",
    DESTACADA),
  estudio("embarazo-sentada", ...V, "Sesión de embarazo",
    "Embarazada sentada en un cubo blanco con las piernas cruzadas y los ojos cerrados",
    DESTACADA),
  estudio("embarazo-tiara", ...V, "Sesión de embarazo",
    "Embarazada con una tiara y un blazer sobre los hombros mira su barriga sonriendo",
    DESTACADA),
  evento("revelacion-torta", ...V, "Revelación de sexo",
    "Los futuros papás abrazados junto a la torta, frente a un arco de globos pastel",
    DESTACADA),
  estudio("embarazo-risa", ...V, "Sesión de embarazo",
    "Embarazada riendo hacia un lado con las manos bajo la barriga, en blanco y negro",
    DESTACADA),
  estudio("embarazo-cabello", ...V, "Sesión de embarazo",
    "Embarazada de perfil con el brazo sobre la cabeza y el cabello suelto sobre la espalda"),
  estudio("embarazo-mano-en-el-pelo", ...V, "Sesión de embarazo",
    "Embarazada con una tiara y una mano en el pelo, con los ojos cerrados"),
  estudio("embarazo-blazer", ...V, "Sesión de embarazo",
    "Embarazada con blazer negro abierto y los ojos cerrados, con una mano en la cintura"),
  evento("embarazo-manos", 3802, 3456, "Revelación de embarazo",
    "La pareja se toma de la mano y se mira riendo, con unos calcetines de bebé colgando entre los dos"),
  evento("revelacion-tablero", ...H, "Revelación de sexo",
    "Los futuros papás sonríen a cámara abrazados junto al tablero de votos niño o niña"),
  evento("revelacion-voto", ...H, "Revelación de sexo",
    "La futura mamá pega un sticker celeste en el tablero de votos"),
  evento("baby-shower-postres", ...H, "Baby shower",
    "Mesa de postres con cupcakes y vasitos de crema decorados en celeste"),
  evento("revelacion-decoracion", ...V, "Revelación de sexo",
    "Arco de globos pastel, un oso de peluche y cubos con la palabra BABY"),

  /*
   * Revelación de sexo del 10·10·2026 (entrega /entregas/revelacion-sexo-10-10-2026).
   * La clienta marcó «sí» al portafolio en el formulario de experiencia
   * (12·10·2026). Antes del deploy tienen que estar en omega_autorizaciones la
   * firma de ella y la de su pareja, con uso_portafolio en true
   * (docs/proceso-entrega.md, paso 5).
   *
   * Solo van encuadres donde aparecen ellos dos. Quedan fuera las fotos con
   * familiares e invitados: cada uno tendría que firmar.
   */
  evento("revelacion-nina-confeti", 4156, 2771, "Revelación de sexo",
    "Lluvia de confeti rosado sobre los futuros papás en el momento en que se revela que es niña"),
  evento("revelacion-nina-retrato", ...H, "Revelación de sexo",
    "Él abraza por detrás a la futura mamá, que lleva una banda rosada, frente a un fondo de globos blancos y dorados"),
  evento("revelacion-nina-marco", ...H, "Revelación de sexo",
    "Los futuros papás se besan dentro de un marco de cartón que dice «¿Niña o niño?»"),
  evento("revelacion-nina-corazon", ...H, "Revelación de sexo",
    "Las manos de la pareja forman un corazón sobre la barriga de la futura mamá"),
  evento("revelacion-nina-detonador", ...H, "Revelación de sexo",
    "Los futuros papás presionan juntos el detonador entre chispas, frente a letras luminosas"),
  evento("revelacion-nina-beso", ...H, "Revelación de sexo",
    "Beso de los futuros papás bajo las chispas, con confeti rosado en el pelo"),

  /*
   * Revelación de sexo del 11·10·2026 (entrega /entregas/revelacion-sexo-11-10-2026).
   * Se publica por decisión expresa del estudio (22·10·2026). Antes del deploy
   * tienen que estar en omega_autorizaciones la firma de ella y la de su
   * pareja, con uso_portafolio en true (docs/proceso-entrega.md, paso 5).
   *
   * Solo van encuadres de los dos o de la decoración. Queda fuera la de la
   * familiar con la mano en la barriga: tendría que firmar.
   *
   * Los archivos son copia de los de la entrega: /fotografia/entregas/* va
   * con noindex por cabecera, y aquí se necesitan indexables.
   */
  eventoNuevo("revelacion-nino-momento", 5184, 3456, "Revelación de sexo",
    "Él celebra con el puño en alto y la futura mamá se tapa la boca mientras el humo azul revela que es niño"),
  eventoNuevo("revelacion-nino-laguna", 4608, 3456, "Revelación de sexo",
    "Los futuros papás se miran sonriendo junto a la laguna, con las manos de ambos sobre la barriga"),
  eventoNuevo("revelacion-nino-vestido", 4608, 3456, "Revelación de sexo",
    "La futura mamá, con vestido de encaje, se toma la barriga y mira hacia la laguna"),
  eventoNuevo("revelacion-nino-sendero", 5184, 3456, "Revelación de sexo",
    "Los futuros papás caminan de la mano por un sendero entre árboles y cercos blancos"),
  eventoNuevo("revelacion-nino-muelle", 4608, 3456, "Revelación de sexo",
    "Él, arrodillado en el muelle, besa la barriga de la futura mamá"),
  eventoNuevo("revelacion-nino-corazon", 4490, 3368, "Revelación de sexo",
    "Las manos de los dos forman un corazón sobre la barriga"),
  eventoNuevo("revelacion-nino-beso", 5184, 3456, "Revelación de sexo",
    "Los futuros papás se besan en el sendero, con las manos sobre la barriga"),
  eventoNuevo("revelacion-nino-decoracion", 5184, 3456, "Revelación de sexo",
    "Letras luminosas OH BABY con globos celestes, rosados y dorados, entre chispas y el detonador al centro"),
  eventoNuevo("revelacion-nino-detonador", 5184, 3456, "Revelación de sexo",
    "Los futuros papás sonríen junto al detonador frente a las letras OH BABY"),
  eventoNuevo("revelacion-nino-humo", 4216, 2811, "Revelación de sexo",
    "El humo azul se eleva sobre las letras OH BABY mientras los futuros papás presionan el detonador"),
  eventoNuevo("revelacion-nino-emocion", 4748, 3165, "Revelación de sexo",
    "Él ríe mirando a la futura mamá, que se cubre el rostro emocionada"),
  eventoNuevo("revelacion-nino-abrazo", 5184, 3456, "Revelación de sexo",
    "Los futuros papás se abrazan riendo, con el humo azul de fondo"),

  /*
   * BODAS — boda civil del 26·09·2026. Vienen de la entrega privada
   * (data/entregas.js) y se publican por decisión expresa del estudio
   * (11·10·2026). La autorización de uso de imagen de los novios tiene que
   * quedar registrada en omega_autorizaciones (docs/proceso-entrega.md).
   *
   * Solo van encuadres de los novios o de detalle. Quedan fuera las fotos con
   * otros invitados y niños en primer plano, y la de la libreta de
   * matrimonio, que muestra el número del documento.
   *
   * Los archivos son copia de los de la entrega: /fotografia/entregas/* va
   * con noindex por cabecera, y aquí se necesitan indexables.
   */
  boda("beso", ...H, "Boda civil",
    "Los novios se besan al terminar la ceremonia de su boda civil"),
  boda("anillos", ...H, "Boda civil",
    "Las manos de los novios, una sobre la otra, muestran los anillos"),
  boda("miradas", ...H, "Boda civil",
    "Los novios frente a frente, mirándose, a punto de besarse"),
  boda("emocion", ...H, "Boda civil",
    "La novia emocionada, con los ojos húmedos, durante la ceremonia"),
  boda("risas", ...H, "Boda civil",
    "Los novios ríen durante la ceremonia frente a un muro verde"),
  boda("abrazo", 3456, 2304, "Boda civil",
    "La novia abraza al novio y mira a cámara"),
  boda("novia", ...H, "Boda civil",
    "Retrato de la novia con vestido crema sobre fondo blanco"),
  boda("flores", ...H, "Boda civil",
    "Arreglo de flores blancas sobre la mesa de la ceremonia"),
];

/** Fotos de un segmento, en el orden del manifiesto. */
export const fotosDe = (segmentoId) =>
  FOTOS.filter((foto) => foto.segmento === segmentoId);

/** Las marcadas como destacadas, para hero y tira de prueba visual. */
export const fotosDestacadas = (limite = 10) =>
  FOTOS.filter((foto) => foto.featured).slice(0, limite);

/**
 * Foto de portada de un segmento: la que se pida por id si existe, y si no la
 * primera del segmento. Null si aún no hay ninguna.
 */
export const fotoPrincipal = (segmentoId, id) =>
  FOTOS.find((foto) => foto.id === id) || fotosDe(segmentoId)[0] || null;

/** Un segmento es publicable cuando tiene fotos propias suficientes. */
export const segmentoListo = (segmentoId) =>
  fotosDe(segmentoId).length >= MINIMO_FOTOS;

export const hayPortafolio = () => FOTOS.length > 0;
